import React, { useEffect, useState } from 'react';
import { Phone, X } from 'lucide-react';
import { Button } from '../SubscriptionAppButton';
export type CallOutcome = 'Answered' | 'Voicemail' | 'No answer' | 'Ordered' | 'Not interested';
export interface LoggedCall {
  outcome: CallOutcome;
  note: string;
}
interface LogCallModalProps {
  isOpen: boolean;
  contactName: string;
  onClose: () => void;
  onSave: (call: LoggedCall) => void;
}
const OUTCOMES: CallOutcome[] = ['Answered', 'Voicemail', 'No answer', 'Ordered', 'Not interested'];
const labelClass = 'block text-[12px] font-medium text-[#475569] mb-1.5';

// Quick call log: outcome plus an optional note
export function LogCallModal({
  isOpen,
  contactName,
  onClose,
  onSave
}: LogCallModalProps) {
  const [outcome, setOutcome] = useState<CallOutcome | null>(null);
  const [note, setNote] = useState('');
  useEffect(() => {
    if (!isOpen) return;
    setOutcome(null);
    setNote('');
  }, [isOpen]);
  if (!isOpen) return null;
  const handleSave = () => {
    if (!outcome) return;
    onSave({
      outcome,
      note: note.trim()
    });
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-5" data-id="element-1569" data-mp="1wk1wrpbwov3s">
      <div className="absolute inset-0 bg-[#0F172A]/50" onClick={onClose} data-id="element-1570" data-mp="1hoc79h7ihzub" />
      <div role="dialog" aria-modal="true" aria-labelledby="log-call-title" className="relative bg-white rounded-lg shadow-xl w-full max-w-[520px] flex flex-col max-h-[calc(100vh-40px)]" data-id="element-1571" data-mp="1mmx91l2ewrkv">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E2E8F0]" data-id="element-1572" data-mp="18to35udoao73">
          <div className="flex items-center gap-3" data-id="element-1573" data-mp="1dobr9s4rlgl5">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-[#E0F2FE]" data-id="element-1574" data-mp="yye1j7r6situ">
              <Phone className="w-5 h-5 text-[#0EA5E9]" data-id="element-1575" data-mp="13xh7qviwkv3e" />
            </div>
            <div data-id="element-1576" data-mp="pwwk9m21mb3k">
              <h2 id="log-call-title" className="text-[20px] font-semibold text-[#0F172A]" data-id="element-1577" data-mp="pwelahnzxiry">
                Log call
              </h2>
              <p className="text-[13px] text-[#64748B]" data-id="element-1578" data-mp="txghsqqj3gq8">{contactName}</p>
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" className="w-8 h-8 flex items-center justify-center rounded-md text-[#64748B] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition-colors" data-id="element-1579" data-mp="2wmzz6tl4of7f">
            <X className="w-5 h-5" data-id="element-1580" data-mp="2mvdfs9ymz9tl" />
          </button>
        </div>

        <div className="px-6 py-6 overflow-y-auto space-y-5" data-id="element-1581" data-mp="2hx25xz8cczbd">
          <fieldset data-id="element-1582" data-mp="3fjj2315c96vj">
            <legend className={labelClass} data-id="element-1583" data-mp="3fk18gqlpt1h6">
              Outcome <span className="text-[#EF4444]" data-id="element-1584" data-mp="3fhook7wech1h">*</span>
            </legend>
            <div className="flex flex-wrap gap-2" data-id="element-1585" data-mp="3al7qakvhgmao">
              {OUTCOMES.map(option => {
              const isSelected = outcome === option;
              return <button key={option} type="button" aria-pressed={isSelected} onClick={() => setOutcome(option)} className={`h-9 px-3 rounded-md border text-[13px] font-medium transition-colors ${isSelected ? 'bg-[#E0F2FE] border-[#0EA5E9] text-[#0369A1]' : 'bg-white border-[#CBD5E1] text-[#475569] hover:bg-[#F8FAFC]'}`} data-id="element-1586" data-mp="31k9v8s30vp48">
                    {option}
                  </button>;
            })}
            </div>
          </fieldset>

          <div data-id="element-1587" data-mp="9p73tnqp0c0e">
            <label htmlFor="log-call-note" className={labelClass} data-id="element-1588" data-mp="9oxfe4laqcwt">
              Call note <span className="font-normal text-[#94A3B8]" data-id="element-1589" data-mp="emflzobucg1n">(optional)</span>
            </label>
            <textarea id="log-call-note" value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. Left a message about the spring release" rows={3} className="w-full px-3 py-2.5 border border-[#CBD5E1] rounded-md bg-white text-[14px] text-[#0F172A] placeholder:text-[#94A3B8] focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none transition-all resize-none" data-id="element-1590" data-mp="jf8rief4vses" />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#E2E8F0]" data-id="element-1591" data-mp="12cjh17ayr7h1">
          <Button variant="outline" onClick={onClose} data-id="element-1592" data-mp="2z6wcdbgvhuh8">
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={!outcome} data-id="element-1593" data-mp="344y6crfu7e7h">
            Save call
          </Button>
        </div>
      </div>
    </div>;
}